import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Indonesian mirror of the apostille-vs-legalization guide. */
const TITLE = "Apostille vs legalisasi konsuler untuk dokumen Thailand — panduan per negara";
const DESC =
  "Perbedaan apostille dan legalisasi konsuler untuk dokumen Thailand, urutan langkah yang benar, dan catatan per negara agar dokumen tidak ditolak.";
const PATH = "/blog/apostille-vs-legalization-by-country";
const URL = `/id${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Apa perbedaan apostille dan legalisasi konsuler?",
    answer:
      "Apostille adalah satu sertifikat tunggal yang diakui antar negara anggota Konvensi Den Haag. Legalisasi konsuler memerlukan lapisan tambahan, yaitu pengesahan Departemen Konsuler Thailand lalu pengesahan kedutaan negara tujuan.",
  },
  {
    question: "Bagaimana urutan langkah yang benar?",
    answer:
      "Ambil dokumen terbaru dari instansi penerbit, notarisasi oleh Notarial Services Attorney bila dokumen dibuat sendiri, pengesahan Departemen Konsuler, penerjemahan, lalu legalisasi kedutaan bila diminta.",
  },
  {
    question: "Bolehkah menerjemahkan lebih dulu?",
    answer:
      "Sebaiknya tidak. Terjemahan sebelum lapisan pengesahan selesai sering harus diulang karena cap, stempel, dan catatan pengesahan baru muncul setelahnya.",
  },
  {
    question: "Negara mana yang biasanya meminta legalisasi kedutaan?",
    answer:
      "Beberapa negara di Timur Tengah dan sejumlah negara Asia masih meminta lapisan kedutaan. Selalu konfirmasi daftar resmi instansi penerima sebelum mulai.",
  },
  {
    question: "Berapa lama prosesnya?",
    answer:
      "Bergantung pada jenis dokumen dan jumlah lapisan pengesahan. Faktor terbesar biasanya waktu penerbitan salinan baru dan jadwal kedutaan, bukan penerjemahannya.",
  },
  {
    question: "Apakah salinan fotokopi bisa dipakai?",
    answer:
      "Banyak instansi penerima meminta dokumen asli yang sudah disahkan lengkap. Siapkan satu set salinan tambahan untuk arsip dan langkah paralel.",
  },
  {
    question: "Kenapa dokumen sering ditolak?",
    answer:
      "Penyebab tersering adalah salinan yang sudah kedaluwarsa, urutan pengesahan yang salah, ejaan nama berbeda dari paspor, dan terjemahan yang tidak memuat cap serta stempel.",
  },
];

export const Route = createFileRoute("/id/blog/apostille-vs-legalization-by-country")({
  component: IdApostilleGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "id" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("apostille-vs-legalization-by-country", "id") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "apostille dokumen Thailand, legalisasi konsuler Thailand, pengesahan dokumen Thailand, legalisasi kedutaan",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "id"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Beranda", url: "/id" },
              { name: "Panduan", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("apostille-vs-legalization-by-country"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Apostille & Legalisation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DIFFS: Array<{ d: string; note: string }> = [
  { d: "Jumlah lapisan", note: "Apostille satu sertifikat; legalisasi konsuler dua lapisan atau lebih" },
  { d: "Instansi yang terlibat", note: "Departemen Konsuler Thailand, lalu kedutaan negara tujuan bila diminta" },
  { d: "Dokumen pribadi", note: "Surat kuasa, pernyataan, dan afidavit perlu notarisasi lebih dulu" },
  { d: "Dokumen instansi", note: "Akta sipil dan dokumen perusahaan cukup diambil versi terbaru lalu disahkan" },
  { d: "Terjemahan", note: "Dilakukan setelah pengesahan agar cap dan stempel ikut diterjemahkan" },
  { d: "Masa berlaku", note: "Banyak instansi hanya menerima salinan yang diterbitkan 3–6 bulan terakhir" },
];

const ORDER: string[] = [
  "Minta daftar resmi dari instansi atau mitra di negara tujuan",
  "Ambil salinan terbaru dokumen dari instansi penerbit di Thailand",
  "Notarisasi oleh Notarial Services Attorney untuk dokumen yang dibuat sendiri",
  "Pengesahan di Departemen Konsuler Kementerian Luar Negeri Thailand",
  "Penerjemahan lengkap termasuk cap, stempel, dan catatan pengesahan",
  "Legalisasi di kedutaan negara tujuan bila diminta, lalu periksa nama dan tanggal",
];

const MISTAKES: string[] = [
  "Menerjemahkan sebelum lapisan pengesahan selesai",
  "Memakai salinan lama yang sudah melewati batas waktu penerimaan",
  "Ejaan nama berbeda antara paspor, akta, dan terjemahan",
  "Terjemahan tidak memuat cap dan stempel",
  "Melewatkan notarisasi untuk dokumen yang dibuat sendiri",
  "Jumlah set dokumen kurang dari yang diminta instansi penerima",
];

function IdApostilleGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/id" className="text-muted-foreground hover:text-foreground">
          Beranda
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Panduan
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="apostille-vs-legalization-by-country" locale="id" />
      <p className="answer-summary speakable-summary">
        Apostille adalah satu sertifikat yang diakui antar negara anggota Konvensi Den Haag, sedangkan legalisasi konsuler
        menuntut lapisan tambahan di Departemen Konsuler Thailand dan kedutaan negara tujuan. Kunci agar tidak ditolak
        adalah urutannya: ambil salinan baru, notarisasi bila dokumen dibuat sendiri, sahkan, baru terjemahkan.
      </p>

      <h2>1. Perbedaan utama</h2>
      <ul>
        {DIFFS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Urutan langkah</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Penyebab dokumen ditolak</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Pertanyaan yang sering diajukan</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Rujukan dan layanan terkait</h2>
      <p className="text-sm">
        Rujukan:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Departemen Konsuler, Kementerian Luar Negeri Thailand
        </a>{" "}
        ·{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" target="_blank" rel="noreferrer">
          HCCH — Apostille Section
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/id/lgz" className="text-primary underline">
            Layanan pengesahan dan legalisasi dokumen
          </Link>
        </li>
        <li>
          <Link to="/id/notary" className="text-primary underline">
            Notarisasi oleh Notarial Services Attorney
          </Link>
        </li>
        <li>
          <Link to="/id/corporate" className="text-primary underline">
            Dokumen perusahaan untuk dipakai di luar negeri
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Ditinjau pada {PUBLISHED}. Persyaratan tiap instansi dan negara dapat berbeda, jadi konfirmasi daftar resmi
        sebelum mulai. Jika Anda tidak ingin mengurus tiap lapisan sendiri, tim kami dengan pengalaman lebih dari 15 tahun
        dapat menjadi konsultan sekaligus menangani seluruh prosesnya — hubungi kami lewat telepon, LINE, atau email.
      </p>
    </article>
  );
}
